import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withKeys as _withKeys, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "timetableSearchInputWrapper" }
const _hoisted_2 = { class: "from" }
const _hoisted_3 = ["id"]
const _hoisted_4 = ["value", "aria-labelledby", "aria-expanded", "placeholder", "disabled"]
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = ["id"]
const _hoisted_7 = ["value", "aria-labelledby", "aria-expanded", "placeholder", "disabled"]

import {
  ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import vClickOutside from '@venegrad/vue3-click-outside';
import { uniqBy } from 'lodash';
import { RecentOrSuggestedSearchEntry, SuggestionPortType } from '../../types';
import { mapRecentSearches, mapPortPairToRecentOrSuggestedSearchEntry } from '../../maps';
import { Icon, Card } from '../../Atoms';
import { RecentSearches } from '../../Molecules';
import { IconText } from '..';
import { useTimetableStore } from '../../store';
import { SEARCH_CONFIG } from '../../../../constants';


export default /*@__PURE__*/_defineComponent({
  __name: 'TimetableSearchInput',
  props: {
    from: {},
    fromCode: {},
    fromLabel: {},
    fromPlaceholder: {},
    to: {},
    toCode: {},
    toLabel: {},
    toPlaceholder: {},
    disabled: { type: Boolean },
    error: { type: Boolean },
    isDuplicateRoute: { type: Boolean }
  },
  emits: ['updateFrom', 'updateTo', 'updateFromCode', 'updateToCode', 'updateRouteId', 'updateRouteCode', 'searchClick', 'dropdownVisibilityChange'],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();

const props = __props;

// Internal component state
const componentState = ref({
  switched: false,
  from: props.from ? props.from : '',
  fromCode: '',
  fromPlaceholder: props.fromPlaceholder,
  isFromFocus: false,
  to: props.to ? props.to : '',
  toCode: '',
  toPlaceholder: props.toPlaceholder,
  isToFocus: false,
  tempFrom: '',
  tempFromCode: '',
  tempTo: '',
  tempToCode: '',
  routeId: '',
  routeCode: '',
  showSearches: false,
  suggestions: [] as Array<RecentOrSuggestedSearchEntry>,
  suggestionsTitle: '',
  searchSelected: !!(props.from && props.to),
});

const fromInputRef = ref<HTMLInputElement | null>(null);
const toInputRef = ref<HTMLInputElement | null>(null);
const activeInputRef = ref<HTMLInputElement | null>(null);

// Emittable events

const emit = __emit;

// Initialise the store

const timetableStore = useTimetableStore();

// Async functions for managing external state

const getSuggestions = async (
  portType: SuggestionPortType,
  newValue: string,
) => {
  componentState.value.suggestionsTitle = newValue.length < SEARCH_CONFIG.MIN_SEARCH_LENGTH ? t('Recent') : t('Select a route');
  componentState.value = { ...componentState.value, showSearches: true };
  if (newValue.length < SEARCH_CONFIG.MIN_SEARCH_LENGTH) {
    const recent = timetableStore.recentSearches;
    const recentSuggestions = recent?.map((item) => mapRecentSearches(item));
    const shouldShowHelperTip = !componentState.value.from && !componentState.value.to;
    componentState.value = {
      ...componentState.value,
      showSearches: shouldShowHelperTip
        || (recentSuggestions !== undefined && recentSuggestions.length > 0),
    };
    if (recentSuggestions) {
      componentState.value.suggestions = recentSuggestions;
    }
  } else if (portType === SuggestionPortType.Origin) {
    try {
      await timetableStore.getSuggestionsByOriginPortName(newValue);
    } catch (e) {
      console.log('e', e);
    }
  } else if (portType === SuggestionPortType.Destination) {
    try {
      await timetableStore.getSuggestionsByDestinationPortName(newValue);
    } catch (e) {
      console.log('e', e);
    }
  }
};

const onOriginChanged = async (newValue: string) => {
  componentState.value = {
    ...componentState.value,
    from: newValue,
    searchSelected: !!(newValue && componentState.value.to),
  };
  await getSuggestions(SuggestionPortType.Origin, newValue);
};

const onDestinationChanged = async (newValue: string) => {
  componentState.value = {
    ...componentState.value,
    searchSelected: !!(newValue && componentState.value.from),
    to: newValue,
  };
  await getSuggestions(SuggestionPortType.Destination, newValue);
};

const switchLocations = () => {
  if (props.disabled) {
    return;
  }
  componentState.value.switched = !componentState.value.switched;
};

const onToKeydown = (event: KeyboardEvent) => {
  if (!componentState.value.showSearches) return;

  if (event.key === 'Tab') {
    if (event.shiftKey) {
      componentState.value.showSearches = false;
    } else {
      event.preventDefault();
      const dropdown = document.querySelector('.searchAutocomplete');
      const firstFocusable = dropdown?.querySelector('button, [tabindex="0"]');
      (firstFocusable as HTMLElement)?.focus();
    }
  }
};

function isMobileDevice() {
  return typeof window !== 'undefined' && 'ontouchstart' in window;
}

const focusDropdown = () => {
  if (isMobileDevice()) {
    return;
  }

  const dropdown = document.querySelector('.searchAutocomplete');
  const firstFocusable = dropdown?.querySelector('button, [tabindex="0"]');
  (firstFocusable as HTMLElement)?.focus();
};

const updateEmits = () => {
  emit('updateFrom', componentState.value.from);
  emit('updateFromCode', componentState.value.fromCode);
  emit('updateTo', componentState.value.to);
  emit('updateToCode', componentState.value.toCode);
  emit('updateRouteId', componentState.value.routeId);
  emit('updateRouteCode', componentState.value.routeCode);
};

function handleFocus() {
  // eslint-disable-next-line max-len
  const focusComingFromPortSelection = componentState.value.isFromFocus || componentState.value.isToFocus;

  componentState.value = {
    ...componentState.value,
    searchSelected: false,
    tempFrom: focusComingFromPortSelection
      ? componentState.value.tempFrom
      : componentState.value.tempFrom || componentState.value.from,
    tempFromCode: focusComingFromPortSelection
      ? componentState.value.tempFromCode
      : componentState.value.tempFromCode || componentState.value.fromCode,
    tempTo: focusComingFromPortSelection
      ? componentState.value.tempTo
      : componentState.value.tempTo || componentState.value.to,
    tempToCode: focusComingFromPortSelection
      ? componentState.value.tempToCode
      : componentState.value.tempToCode || componentState.value.toCode,
  };
}

function handleBlur() {
  if (componentState.value.tempFrom !== '' || componentState.value.tempTo !== '') {
    componentState.value = {
      ...componentState.value,
      from: componentState.value.tempFrom,
      fromCode: componentState.value.tempFromCode,
      fromPlaceholder: props.fromPlaceholder,
      to: componentState.value.tempTo,
      toCode: componentState.value.tempToCode,
      toPlaceholder: props.toPlaceholder,
      searchSelected: !!(componentState.value.tempFromCode && componentState.value.tempToCode),
      tempFrom: '',
      tempFromCode: '',
      tempTo: '',
      tempToCode: '',
    };
  } else if (componentState.value.searchSelected === false) {
    componentState.value = {
      ...componentState.value,
      from: '',
      fromCode: '',
      fromPlaceholder: props.fromPlaceholder,
      to: '',
      toCode: '',
      toPlaceholder: props.toPlaceholder,
      routeId: componentState.value.routeId,
      routeCode: componentState.value.routeCode,
    };
  } else {
    updateEmits();
  }
  componentState.value.showSearches = false;
  componentState.value.isFromFocus = false;
  componentState.value.isToFocus = false;
  timetableStore.clearSuggestions();
}

const onOriginFocused = async () => {
  componentState.value = {
    ...componentState.value,
    from: '',
    to: '',
    searchSelected: false,
  };
  handleFocus();
  componentState.value.isFromFocus = true;
  updateEmits();
  await getSuggestions(SuggestionPortType.Origin, '');
};

const onDestinationFocused = async () => {
  handleFocus();
  componentState.value.isToFocus = true;
  await getSuggestions(SuggestionPortType.Destination, '');
};

// Recents list event handlers

const setSearchValue = (portCoupling: RecentOrSuggestedSearchEntry) => {
  componentState.value = {
    ...componentState.value,
    from: portCoupling.ports[0].name,
    fromCode: portCoupling.ports[0].portCode ?? '',
    to: portCoupling.ports[1].name,
    toCode: portCoupling.ports[1].portCode ?? '',
    tempFrom: '',
    tempFromCode: '',
    tempTo: '',
    tempToCode: '',
    routeId: portCoupling.routeId,
    routeCode: portCoupling.routeCode ? portCoupling.routeCode : '',
    searchSelected: true,
    isFromFocus: false,
    isToFocus: false,
    showSearches: false,
  };
  updateEmits();
};

const handleDeleteSearch = (search: RecentOrSuggestedSearchEntry) => {
  timetableStore.deleteRecentSearch(search);
  const recent = timetableStore.getRecentSearches();
  componentState.value.suggestions = recent?.map((item) => mapRecentSearches(item)) || [];
};

watch(() => props.from, (from) => {
  if (from !== undefined) {
    componentState.value = {
      ...componentState.value,
      from,
    };
  }
});

watch(() => props.to, (to) => {
  if (to !== undefined) {
    componentState.value = {
      ...componentState.value,
      to,
    };
  }
});

watch(() => timetableStore.portPairSuggestions, (portPairs) => {
  componentState.value.suggestions = Array.isArray(portPairs) && portPairs.length > 0
    ? uniqBy(portPairs, (r) => `${r.originPort}-${r.destinationPort}`).map(mapPortPairToRecentOrSuggestedSearchEntry)
    : [];
});

watch(() => componentState.value.switched, () => {
  const tempFrom = componentState.value.from;
  const tempFromCode = componentState.value.fromCode;
  const tempTo = componentState.value.to;
  const tempToCode = componentState.value.toCode;
  componentState.value.from = tempTo;
  componentState.value.fromCode = tempToCode;
  componentState.value.to = tempFrom;
  componentState.value.toCode = tempFromCode;
  updateEmits();
});

watch(() => componentState.value.showSearches, (newValue) => {
  emit('dropdownVisibilityChange', newValue);
});


return (_ctx: any,_cache: any) => {
  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Card), {
      class: _normalizeClass([
        'timetableSearchInput',
        {
          disabled: _ctx.disabled,
          error: _ctx.error || _ctx.isDuplicateRoute,
          isFocus: (componentState.value.isFromFocus || componentState.value.isToFocus)
            && componentState.value.showSearches
        },
      ])
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(IconText), {
            class: "placeholder",
            "icon-name": "searchMapPin",
            "icon-lg": true
          }),
          _createElementVNode("label", {
            id: _ctx.fromLabel,
            for: "from",
            class: "timetableLabel"
          }, _toDisplayString(_ctx.fromLabel), 9, _hoisted_3),
          _createElementVNode("input", {
            id: "from",
            ref_key: "fromInputRef",
            ref: fromInputRef,
            role: "combobox",
            type: "text",
            name: "from",
            value: componentState.value.from,
            "aria-controls": "timetableSuggestions",
            autocomplete: "off",
            "aria-haspopup": "listbox",
            "aria-labelledby": _ctx.fromLabel,
            "aria-expanded": componentState.value.showSearches,
            placeholder: componentState.value.fromPlaceholder,
            disabled: _ctx.disabled,
            onFocus: _cache[0] || (_cache[0] = () => { onOriginFocused(); activeInputRef.value = fromInputRef.value; }),
            onInput: _cache[1] || (_cache[1] = ($event: any) => (onOriginChanged(($event.target as HTMLInputElement)?.value))),
            onKeydown: onToKeydown
          }, null, 40, _hoisted_4)
        ]),
        _createElementVNode("button", {
          type: "button",
          class: _normalizeClass(["switch", { fromFocus: componentState.value.isFromFocus && componentState.value.showSearches }]),
          "aria-label": _ctx.$t('Switch button aria label'),
          onClick: switchLocations,
          onKeypress: _withKeys(switchLocations, ["enter"])
        }, [
          _createVNode(_unref(Icon), {
            "icon-name": "switch",
            "aria-hidden": "true"
          })
        ], 42, _hoisted_5),
        _createElementVNode("div", {
          class: _normalizeClass(["to", { fromFocus: componentState.value.isFromFocus && componentState.value.showSearches }])
        }, [
          _createVNode(_unref(IconText), {
            class: "placeholder",
            "icon-name": "searchMapPin",
            "icon-lg": true
          }),
          _createElementVNode("label", {
            id: _ctx.toLabel,
            for: "to",
            class: "timetableLabel"
          }, _toDisplayString(_ctx.toLabel), 9, _hoisted_6),
          _createElementVNode("input", {
            id: "to",
            ref_key: "toInputRef",
            ref: toInputRef,
            role: "combobox",
            type: "text",
            name: "to",
            value: componentState.value.to,
            "aria-controls": "timetableSuggestions",
            autocomplete: "off",
            "aria-haspopup": "listbox",
            "aria-labelledby": _ctx.toLabel,
            "aria-expanded": componentState.value.showSearches,
            placeholder: componentState.value.toPlaceholder,
            disabled: _ctx.disabled,
            onFocus: _cache[2] || (_cache[2] = () => { onDestinationFocused(); activeInputRef.value = toInputRef.value; }),
            onInput: _cache[3] || (_cache[3] = ($event: any) => (onDestinationChanged(($event.target as HTMLInputElement)?.value)))
          }, null, 40, _hoisted_7)
        ], 2)
      ]),
      _: 1
    }, 8, ["class"]),
    _createVNode(_unref(RecentSearches), {
      "input-ref": activeInputRef.value,
      class: _normalizeClass({
        isFocus: (componentState.value.isFromFocus || componentState.value.isToFocus)
          && componentState.value.showSearches
      }),
      title: componentState.value.suggestionsTitle,
      "show-searches": componentState.value.showSearches,
      searches: componentState.value.suggestions,
      "show-helper-tip": !componentState.value.from && !componentState.value.to,
      "aria-id": "timetableSuggestions",
      onSetSearchValue: _cache[4] || (_cache[4] = (value) => setSearchValue(value)),
      onDropdownReady: focusDropdown,
      onSetShowSearches: _cache[5] || (_cache[5] = (val) => componentState.value.showSearches = val),
      onDeleteSearch: handleDeleteSearch
    }, null, 8, ["input-ref", "class", "title", "show-searches", "searches", "show-helper-tip"])
  ])), [
    [_unref(vClickOutside), handleBlur]
  ])
}
}

})